import * as Notifications from 'expo-notifications';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';

import { captureException } from '@/libs/logger';
import { isNotificationSupported } from '@/utils/web-notification';

type PermissionStatus = Notifications.PermissionStatus | 'undetermined';

/**
 * Reads + requests the OS push-notification permission.
 *
 * Exposes the live `status` ('granted' | 'denied' | 'undetermined'), refreshed on
 * mount and whenever the app returns to the foreground (so a change made in Settings
 * is picked up). `request` opens the system prompt only while it can still be shown
 * (status undetermined, or denied while the OS will still ask). Once it's blocked for
 * good it stays silent — callers surface a "denied" banner off `isDenied` instead of
 * a settings alert.
 */
export function useNotificationPermission() {
  const [status, setStatus] = useState<PermissionStatus>('undetermined');
  // Resolved once per mount rather than at module scope, so a prerender pass without
  // `window` can't freeze the answer for the real browser session.
  const [supported] = useState(isNotificationSupported);

  // A prompt can stay open indefinitely, and `refresh` runs on every foreground — so both
  // may resolve after the caller has gone. Skip the state update in that case.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setStatusIfMounted = useCallback((next: PermissionStatus) => {
    if (mountedRef.current) setStatus(next);
  }, []);

  const refresh = useCallback(async (): Promise<PermissionStatus> => {
    // A browser with no Notification API has no status to read, so skip the call — `refresh`
    // runs on mount and on every foreground/tab focus. The status stays 'undetermined'
    // (nothing was ever decided); `isSupported` is what tells callers it can't be.
    if (!supported) return 'undetermined';

    try {
      const current = await Notifications.getPermissionsAsync();
      setStatusIfMounted(current.status);
      return current.status;
    } catch (e) {
      captureException(
        { error_payload: e },
        { tags: { usk_log_type: 'notification_permission_error' } },
      );
      return 'undetermined';
    }
  }, [setStatusIfMounted, supported]);

  // Sync on mount and every time the app comes back to the foreground (e.g. after the
  // user toggles the permission in the system Settings and returns).
  useEffect(() => {
    refresh();
    const sub = AppState.addEventListener('change', state => {
      if (state === 'active') refresh();
    });
    return () => sub.remove();
  }, [refresh]);

  const request = useCallback(async (): Promise<boolean> => {
    try {
      if (Platform.OS === 'web') {
        // Nothing to prompt for when the browser has no Notification API. Callers hide their
        // permission UI in that case, so this is just a guard against a pointless no-op call.
        if (!supported) return false;

        // Prompt straight away, with no awaited work in front of it: browsers only honour
        // requestPermission() while the click that triggered it still counts as a user gesture,
        // so the native path's `getPermissionsAsync()` pre-check can't be reused here. It's
        // safe to skip that check anyway — requestPermission() is idempotent and simply
        // resolves the current value, without prompting, once the user has decided.
        const { status: nextStatus } = await Notifications.requestPermissionsAsync();
        setStatusIfMounted(nextStatus);
        return nextStatus === 'granted';
      }

      const current = await Notifications.getPermissionsAsync();
      if (current.granted) {
        setStatusIfMounted(current.status);
        return true;
      }

      let { status: nextStatus } = current;
      // Prompt only while the OS will still show its dialog; if it's blocked for good
      // stay silent (the caller's banner handles routing to Settings).
      if (nextStatus === 'undetermined' || (nextStatus === 'denied' && current.canAskAgain)) {
        ({ status: nextStatus } = await Notifications.requestPermissionsAsync());
      }
      setStatusIfMounted(nextStatus);
      return nextStatus === 'granted';
    } catch (e) {
      captureException(
        { error_payload: e },
        { tags: { usk_log_type: 'notification_permission_error' } },
      );
      return false;
    }
  }, [setStatusIfMounted, supported]);

  return {
    /** Live OS permission status. */
    status,
    /** True when notifications are turned off at the OS level. */
    isDenied: status === 'denied',
    /** True once notifications are allowed — nothing left to ask or warn about. */
    isGranted: status === 'granted',
    /**
     * True while the permission has never been decided, i.e. `request` can still raise a
     * prompt. Prefer this over comparing `status` at the call site.
     */
    isUndetermined: status === 'undetermined',
    /**
     * Whether calling `request` can still produce a prompt: the platform must support
     * notifications and the permission must not have been decided yet. Callers that only need
     * "should I prompt or explain?" can branch on this alone.
     */
    canAsk: supported && status === 'undetermined',
    /**
     * False only on browsers with no Notification API (e.g. a regular Safari tab on iPad).
     * `request` is a no-op in that case, and callers hide their permission UI entirely.
     */
    isSupported: supported,
    request,
    refresh,
  };
}

export default useNotificationPermission;
